/**
 * Frame tooling overlay (Block C). Host-side only — a floating tool button +
 * flyout, a per-type property panel, the persistent "Vorschau-Anker" badge, and
 * the graduated main-frame warnings. NOTHING here touches the Excalidraw toolbar
 * under packages/ (C.1 explicitly requires a host overlay, not a packages/ edit).
 *
 * Anchor deletion (C.4 → V1.2 Block 0.5): intercepting Excalidraw's delete
 * action pre-hoc would require a packages/ edit (inviolable), so deletion is
 * detected right after it happens. v1.1.2 surfaced that as a BLOCKING modal;
 * V1.2 replaces it with a non-blocking toast + Undo. Rationale (Decision
 * 2026-07-31, same argument as the resize case): a blocking modal is the
 * instrument for an imminent, hard-to-reverse step — post-hoc, with full
 * reversibility via Undo, it only blocks and numbs. The three warning tiers
 * are consistent again: persistent badge (content) → debounced hint (geometry)
 * → toast with Undo (deletion). The pointer falls back to "whole scene" until
 * Undo restores it (B.3.3).
 */

import { sceneCoordsToViewportCoords } from "@excalidraw/excalidraw";
import { useEffect, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { isFrameEl } from "../scene-links";

import "./ryfe.scss";

const geomSig = (el: ExcalidrawElement) =>
  `${Math.round(el.x)}:${Math.round(el.y)}:${Math.round(el.width)}:${Math.round(
    el.height,
  )}`;

/**
 * CC_06 Block 5 — FrameTools is now PASSIVE only: the persistent preview-anchor
 * badge and the two non-blocking lifecycle toasts (resize hint, anchor-deleted +
 * Undo). All frame ACTIONS moved to the Element-Menü. It watches the main-frame
 * pointer, nothing about the current selection.
 */
export const FrameTools = ({
  api,
  mainFrameId,
  setMainFrame,
  viewVersion,
  sceneTick,
  embeddedIn = [],
}: {
  api: ExcalidrawImperativeAPI;
  mainFrameId: string | null;
  setMainFrame: (id: string | null) => void;
  viewVersion: number;
  sceneTick: number;
  // Block 9 — boards embedding this scene (turns warnings into statements).
  embeddedIn?: { id: string; name: string }[];
}) => {
  // 0.5 — id of the just-deleted anchor, so the toast's Undo can restore it.
  const [deletedAnchor, setDeletedAnchor] = useState<string | null>(null);
  const [resizeHint, setResizeHint] = useState(false);
  const anchorGeom = useRef<string | null>(null);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const elements = api.getSceneElementsIncludingDeleted();
  const mainFrame =
    (mainFrameId &&
      elements.find(
        (el) => el.id === mainFrameId && !el.isDeleted && isFrameEl(el),
      )) ||
    null;

  // C.4 — detect the two lifecycle events on the main frame.
  useEffect(() => {
    if (!mainFrameId) {
      anchorGeom.current = null;
      return;
    }
    const el = elements.find((e) => e.id === mainFrameId && isFrameEl(e));
    if (!el || el.isDeleted) {
      // The anchor vanished (deleted). Non-blocking toast + Undo; fall back.
      if (anchorGeom.current !== null) {
        setDeletedAnchor(mainFrameId);
        setMainFrame(null);
        anchorGeom.current = null;
      }
      return;
    }
    const sig = geomSig(el);
    if (anchorGeom.current === null) {
      anchorGeom.current = sig;
    } else if (anchorGeom.current !== sig) {
      // Resize/move of the anchor — non-blocking, debounced (≈ one per gesture).
      anchorGeom.current = sig;
      if (hintTimer.current) {
        clearTimeout(hintTimer.current);
      }
      hintTimer.current = setTimeout(() => setResizeHint(true), 350);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneTick, mainFrameId]);

  useEffect(() => {
    if (!resizeHint) {
      return;
    }
    const t = setTimeout(() => setResizeHint(false), 2600);
    return () => clearTimeout(t);
  }, [resizeHint]);

  // 0.5 — the delete-toast auto-dismisses; long enough to reach the Undo.
  useEffect(() => {
    if (!deletedAnchor) {
      return;
    }
    const t = setTimeout(() => setDeletedAnchor(null), 8000);
    return () => clearTimeout(t);
  }, [deletedAnchor]);

  // 0.5 — Undo: restore the deleted frame element and re-set the pointer.
  // Fully host-side: the element still exists with isDeleted=true, so flipping
  // it back (with a version bump so collab reconciliation accepts it) restores
  // exactly the pre-delete state.
  const undoAnchorDelete = () => {
    const id = deletedAnchor;
    if (!id) {
      return;
    }
    const els = api.getSceneElementsIncludingDeleted();
    const el = els.find((e) => e.id === id);
    if (el && el.isDeleted) {
      api.updateScene({
        elements: els.map((e) =>
          e.id === id
            ? {
                ...e,
                isDeleted: false,
                version: e.version + 1,
                versionNonce: (e.versionNonce + 1) | 0,
              }
            : e,
        ),
      });
      anchorGeom.current = geomSig(el);
      setMainFrame(id);
    }
    setDeletedAnchor(null);
  };

  // Badge position over the main frame (follows pan/zoom via viewVersion).
  const badge = (() => {
    if (!mainFrame) {
      return null;
    }
    const state = api.getAppState();
    const tl = sceneCoordsToViewportCoords(
      { sceneX: mainFrame.x, sceneY: mainFrame.y },
      state,
    );
    return {
      left: tl.x - (state.offsetLeft ?? 0),
      top: tl.y - (state.offsetTop ?? 0),
    };
  })();

  return (
    <>
      {/* CC_06 Block 5 — die frame-spezifischen AKTIONEN (Umbenennen, Verknüpfen,
          Bild/Größe, Vorschau-Anker) sind ins Element-Menü (ElementMenu)
          gewandert. FrameTools trägt nur noch die PASSIVEN Hinweise: das
          Anker-Badge, den Resize-Hinweis und den Lösch-Toast mit Undo. Die
          Frame-Erstellung bleibt beim nativen Excalidraw-Werkzeug. */}

      {/* C.4 — persistent badge on the main frame */}
      {badge && (
        <div
          className="ryfe-anchor-badge"
          style={{ left: badge.left, top: badge.top }}
          data-v={viewVersion}
        >
          ★ Vorschau-Anker
        </div>
      )}

      {/* C.4 — non-blocking resize/move hint. Block 9 turns the subjunctive
          into a statement: the reverse index names the embedding boards. */}
      {resizeHint && (
        <div className="ryfe-frame-toast" role="status">
          {embeddedIn.length > 0
            ? `Vorschau-Ausschnitt geändert — diese Szene ist in ${
                embeddedIn.length
              } Board${
                embeddedIn.length > 1 ? "s" : ""
              } eingebettet: ${embeddedIn.map((b) => b.name).join(", ")}.`
            : "Vorschau-Ausschnitt geändert — derzeit in keinem Board eingebettet."}
        </div>
      )}

      {/* 0.5 — non-blocking toast with Undo when the anchor was deleted */}
      {deletedAnchor && (
        <div
          className="ryfe-frame-toast ryfe-frame-toast--undo"
          role="status"
          data-testid="anchor-deleted-toast"
        >
          {embeddedIn.length > 0
            ? `Vorschau-Anker gelöscht — betrifft ${
                embeddedIn.length
              } einbettende${embeddedIn.length > 1 ? "" : "s"} Board${
                embeddedIn.length > 1 ? "s" : ""
              } (${embeddedIn
                .map((b) => b.name)
                .join(", ")}); die Vorschau zeigt jetzt die ganze Szene.`
            : "Vorschau-Anker gelöscht — die Vorschau zeigt jetzt die ganze Szene."}
          <button
            className="ryfe-btn ryfe-btn--link"
            onClick={undoAnchorDelete}
          >
            Rückgängig
          </button>
        </div>
      )}
    </>
  );
};
